import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { X } from "lucide-react";

// In Action images
import inAction1 from "@/assets/javits-b2b/gallery/in-action-1.jpg";
import inAction2 from "@/assets/javits-b2b/gallery/in-action-2.jpg";
import inAction3 from "@/assets/javits-b2b/gallery/in-action-3.jpg";
import inAction4 from "@/assets/javits-b2b/gallery/in-action-4.jpg";
import inAction5 from "@/assets/javits-b2b/gallery/in-action-5.jpg";
import inAction6 from "@/assets/javits-b2b/gallery/in-action-6.jpg";

// Exteriors images
import exterior1 from "@/assets/javits-b2b/gallery/exterior-1.jpg";
import exterior2 from "@/assets/javits-b2b/gallery/exterior-2.jpg";
import exterior3 from "@/assets/javits-b2b/gallery/exterior-3.jpg";
import exterior4 from "@/assets/javits-b2b/gallery/exterior-4.jpg";
import exterior5 from "@/assets/javits-b2b/gallery/exterior-5.jpg";
import exterior6 from "@/assets/javits-b2b/gallery/exterior-6.jpg";
import exterior7 from "@/assets/javits-b2b/gallery/exterior-7.jpg";

// Interiors images
import interior1 from "@/assets/javits-b2b/gallery/interior-1.jpg";
import interior2 from "@/assets/javits-b2b/gallery/interior-2.jpg";
import interior3 from "@/assets/javits-b2b/gallery/interior-3.jpg";
import interior4 from "@/assets/javits-b2b/gallery/interior-4.jpg";
import interior5 from "@/assets/javits-b2b/gallery/interior-5.jpg";

// Tech & Specs images
import tech1 from "@/assets/javits-b2b/gallery/tech-1.jpg";
import tech2 from "@/assets/javits-b2b/gallery/tech-2.jpg";
import tech3 from "@/assets/javits-b2b/gallery/tech-3.jpg";
import tech4 from "@/assets/javits-b2b/gallery/tech-4.jpg";
import tech5 from "@/assets/javits-b2b/gallery/tech-5.jpg";
import tech6 from "@/assets/javits-b2b/gallery/tech-6.jpg";

type GalleryCategory = "all" | "in-action" | "exteriors" | "interiors" | "tech-specs";

// Aspect ratios that tile perfectly in the grid:
// "1:1" = 1 column × 1 row (base unit)
// "16:9" = 2 columns × 1 row (wide)
// "9:16" = 1 column × 2 rows (portrait)
type GridAspectRatio = "1:1" | "16:9" | "9:16";

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  category: GalleryCategory;
  aspectRatio: GridAspectRatio;
}

const galleryImages: GalleryImage[] = [
  // In Action - adjust aspect ratios to match your actual images
  { id: "ia-1", src: inAction1, alt: "Two professionals collaborating in a Quiet Corner pod", category: "in-action", aspectRatio: "1:1" },
  { id: "ia-2", src: inAction2, alt: "Business meeting inside a Quiet Corner pod", category: "in-action", aspectRatio: "1:1" },
  { id: "ia-3", src: inAction3, alt: "Professional working on laptop in single pod", category: "in-action", aspectRatio: "1:1" },
  { id: "ia-4", src: inAction4, alt: "Solo work session in a Quiet Corner pod", category: "in-action", aspectRatio: "9:16" },
  { id: "ia-5", src: inAction5, alt: "Productive meeting in a Quiet Corner pod", category: "in-action", aspectRatio: "1:1" },
  { id: "ia-6", src: inAction6, alt: "Meeting pod in use at Javits Center", category: "in-action", aspectRatio: "16:9" },
  
  // Exteriors
  { id: "ext-1", src: exterior1, alt: "Row of Quiet Corner pods at Javits Center", category: "exteriors", aspectRatio: "16:9" },
  { id: "ext-2", src: exterior2, alt: "Pod exterior with passersby in black and white", category: "exteriors", aspectRatio: "16:9" },
  { id: "ext-3", src: exterior3, alt: "Fleet of pods at Javits Center", category: "exteriors", aspectRatio: "16:9" },
  { id: "ext-4", src: exterior4, alt: "Single pod exterior with open door", category: "exteriors", aspectRatio: "9:16" },
  { id: "ext-5", src: exterior5, alt: "Pod exterior showing interior seating", category: "exteriors", aspectRatio: "9:16" },
  { id: "ext-6", src: exterior6, alt: "Meeting pod exterior with doors open", category: "exteriors", aspectRatio: "1:1" },
  { id: "ext-7", src: exterior7, alt: "Meeting pod with display screen visible", category: "exteriors", aspectRatio: "1:1" },
  
  // Interiors
  { id: "int-1", src: interior1, alt: "Pod interior with booth seating and display screen", category: "interiors", aspectRatio: "16:9" },
  { id: "int-2", src: interior2, alt: "Meeting pod interior with table and monitor", category: "interiors", aspectRatio: "16:9" },
  { id: "int-3", src: interior3, alt: "Pod interior showing tech console and seating", category: "interiors", aspectRatio: "16:9" },
  { id: "int-4", src: interior4, alt: "Single seat interior with padded walls", category: "interiors", aspectRatio: "9:16" },
  { id: "int-5", src: interior5, alt: "Pod door with Quiet Corner branding", category: "interiors", aspectRatio: "1:1" },
  
  // Tech & Specs
  { id: "tech-1", src: tech1, alt: "Control panel with power outlets and timer display", category: "tech-specs", aspectRatio: "1:1" },
  { id: "tech-2", src: tech2, alt: "Press to exit button with LED indicator", category: "tech-specs", aspectRatio: "1:1" },
  { id: "tech-3", src: tech3, alt: "Tech console with timer and USB ports", category: "tech-specs", aspectRatio: "1:1" },
  { id: "tech-4", src: tech4, alt: "Booking kiosk with Stripe payment terminal", category: "tech-specs", aspectRatio: "1:1" },
  { id: "tech-5", src: tech5, alt: "Smart display showing light and temperature controls", category: "tech-specs", aspectRatio: "9:16" },
  { id: "tech-6", src: tech6, alt: "Quiet Corner branding on glass door", category: "tech-specs", aspectRatio: "9:16" },
];

const categories: { value: GalleryCategory; label: string }[] = [
  { value: "all", label: "All" },
  { value: "in-action", label: "In Action" },
  { value: "exteriors", label: "Exteriors" },
  { value: "interiors", label: "Interiors" },
  { value: "tech-specs", label: "Tech & Specs" },
];

const ExploreGallery = () => {
  const [activeCategory, setActiveCategory] = useState<GalleryCategory>("all");
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const filteredImages = activeCategory === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  // Grid classes for gap-free tiling
  const getGridClass = (aspectRatio: GridAspectRatio) => {
    switch (aspectRatio) {
      case "16:9":
        return "col-span-2 row-span-1"; // Wide: 2 columns, 1 row
      case "9:16":
        return "col-span-1 row-span-2"; // Portrait: 1 column, 2 rows
      case "1:1":
      default:
        return "col-span-1 row-span-1"; // Square: 1 column, 1 row
    }
  };

  // Aspect ratio classes for proper image sizing
  const getAspectClass = (aspectRatio: GridAspectRatio) => {
    switch (aspectRatio) {
      case "16:9":
        return "aspect-video"; // 16:9
      case "9:16":
        return "aspect-[9/16]"; // 9:16
      case "1:1":
      default:
        return "aspect-square"; // 1:1
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 bg-neutral-950">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4">
            Explore the Experience.
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            See how our pods transform any space into a premium sanctuary.
          </p>
        </div>

        {/* Filter Tabs - Horizontally scrollable on mobile */}
        <div className="mb-10">
          <ScrollArea className="w-full">
            <div className="flex justify-center min-w-max px-4 pb-4">
              <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as GalleryCategory)}>
                <TabsList className="bg-neutral-900/50 border border-white/10 p-1 rounded-full">
                  {categories.map((cat) => (
                    <TabsTrigger
                      key={cat.value}
                      value={cat.value}
                      className="px-6 py-2 rounded-full text-white/60 data-[state=active]:bg-white data-[state=active]:text-black transition-all duration-300"
                    >
                      {cat.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <ScrollBar orientation="horizontal" className="invisible" />
          </ScrollArea>
        </div>

        {/* Gap-Free Grid - uses fixed row heights for perfect tiling */}
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[200px] sm:auto-rows-[220px] lg:auto-rows-[250px]">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className={`group cursor-pointer overflow-hidden rounded-xl border border-white/10 ${getGridClass(image.aspectRatio)}`}
                onClick={() => setSelectedImage(image)}
              >
                <div className={`relative w-full h-full overflow-hidden ${getAspectClass(image.aspectRatio)}`}>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105 group-hover:brightness-110"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Empty state - will show placeholder until images are added
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden ${
                  i % 5 === 0 ? "row-span-2 aspect-[3/4]" : 
                  i % 7 === 0 ? "col-span-2 aspect-video" : 
                  "aspect-square"
                }`}
              >
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <span className="text-sm">Image {i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox Modal */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-white/10 overflow-hidden">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            {selectedImage && (
              <div className="flex items-center justify-center w-full h-full p-4">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-[85vh] object-contain rounded-lg"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default ExploreGallery;
